<template>
  <div :class="[active.loaded ? '' : 'tw-invisible', { 'tw-dark-layout': isDarkTheme }]"
       class="forgot-page">
    <!-- 內容（教會可用且存在） -->
    <div v-if="active.data.available && active.data.organization.uuid"
         class="tw-flex tw-min-h-screen tw-w-full tw-overflow-hidden tw-bg-white dark:tw-bg-[#161d31]">

      <!-- 品牌 Logo -->
      <a class="tw-absolute tw-left-4 tw-top-4 tw-z-20">
        <img class="tw-h-12 tw-w-12 tw-rounded-md tw-object-cover"
             alt="logo"
             :src="getLogo" />
      </a>

      <!-- 左側：背景圖（大螢幕才顯示） -->
      <div class="tw-relative tw-hidden lg:tw-block lg:tw-flex-1">
        <div class="tw-absolute tw-inset-0 tw-z-[2] tw-bg-white/[0.55] dark:tw-bg-white/[0.25]" />
        <div class="tw-absolute tw-inset-0 tw-z-[1] tw-bg-cover tw-bg-center tw-bg-no-repeat"
             :style="{ backgroundImage: `url(${ getBackground })` }" />
      </div>

      <!-- 右側：表單面板 -->
      <div class="tw-relative tw-flex tw-w-full tw-flex-col tw-justify-center tw-bg-white tw-px-6 tw-py-10 dark:tw-bg-[#343d55] lg:tw-w-[33.333%] lg:tw-min-w-[420px]">
        <div class="tw-mx-auto tw-w-full tw-max-w-md">
          <h2 class="tw-mb-6 tw-text-center tw-text-2xl tw-font-bold tw-text-[#dda026]">
            {{ active.data.name }} - {{ $t('確認郵件驗證碼') }}
          </h2>

          <form @submit.prevent="onSubmit"
                class="tw-flex tw-flex-col tw-items-center tw-gap-3">
            <!-- 六碼驗證碼輸入 -->
            <div class="tw-flex tw-w-full tw-items-center tw-justify-center tw-gap-2">
              <input v-for="j in 6"
                     :key="j"
                     ref="code"
                     class="code-input"
                     maxlength="1"
                     inputmode="numeric"
                     :disabled="getDisabled(j)"
                     @keyup="onKeyUp" />
            </div>
            <p class="tw-w-full tw-text-right tw-text-xs tw-text-[#28c76f]"
               v-html="$t('確認郵件驗證碼')" />

            <!-- 送出 -->
            <button type="button"
                    :disabled="active.xhring || field.code.length !== 6 || active.expire.value < 1"
                    @click="onSubmit"
                    class="f-btn tw-mt-2"
                    v-safe-html="$t(`submit.${ active.xhring ? 'waiting' : 'forgot.verification' }`)" />
          </form>
        </div>

        <!-- 底部：倒數 / 主題 / 語言 / 返回登入 -->
        <div class="tw-mx-auto tw-mt-8 tw-w-full tw-max-w-md">
          <!-- 令牌倒數 -->
          <div v-if="active.expire.value > 0"
               class="tw-mb-2 tw-text-[11px] tw-text-[#b9b9c3]"
               v-safe-html="sprintf($t('本次重設密碼令牌將在X秒後失效'), active.expire.value)" />
          <div v-else
               class="tw-mb-2 tw-text-[11px] tw-text-[#ea5455] tw-opacity-60"
               v-html="$t('本次重設密碼令牌已經失效')" />

          <div class="tw-flex tw-items-center tw-justify-between">
            <div class="tw-flex tw-items-center tw-gap-2.5">
              <!-- 主題切換 -->
              <el-tooltip :content="$t(isDarkTheme ? '深色主題' : '淺色主題')"
                          placement="top-start">
                <icon-sax @click="setTheme(isDarkTheme ? EnumTheme.LIGHT : EnumTheme.DARK)"
                          class="tw-cursor-pointer ft-20"
                          :class="isDarkTheme ? 'tw-text-[#dda026]' : 'tw-text-[#6e6b7b]'"
                          :type="isDarkTheme ? 'sun' : 'moon'" />
              </el-tooltip>
              <!-- 語言 -->
              <el-dropdown popper-class="light-language"
                           placement="top-start">
                <icon-sax class="tw-cursor-pointer tw-text-[#6e6b7b] ft-20"
                          type="global" />
                <template #dropdown>
                  <el-dropdown-menu>
                    <el-dropdown-item v-for="j in getAllRegion"
                                      :key="j.id"
                                      @click="setRegion(j.id)">
                      <span class="tw-flex tw-items-center">
                        <country-flag :iso="j.id" />
                        <span class="tw-ml-2.5 tw-text-xs"
                              :class="getCurrentRegion.id === j.id ? 'tw-text-[#5e5873]' : 'tw-text-[#b9b9c3]'"
                              v-safe-html="j.value" />
                      </span>
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
            </div>
            <!-- 返回登入（放棄重設） -->
            <el-tooltip :content="$t('返回登入')"
                        placement="top-end">
              <icon-sax @click="onDestroy"
                        class="tw-cursor-pointer tw-text-[#6e6b7b] ft-20"
                        type="arrow-right" />
            </el-tooltip>
          </div>
        </div>
      </div>
    </div>

    <!-- 教會不存在 / 不可用 / 令牌失效 -->
    <error404 :home="false"
              v-else />
  </div>
</template>

<script setup>

/**
 * 忘記密碼 - 確認郵件驗證碼
 *
 * 純 Tailwind + JS 重構：六碼輸入改用原生 input，倒數與跳轉邏輯沿用原頁。
 * API 與 input/output 完全沿用原 /api/forgot/verification（後端已扁平化為 kplug）：
 *   - getEdit 讀 data.data.expire（=0 視為失效），成功 code===200 讀 data.data.uuid。
 *   - 驗證失敗回 HTTP 200 {code:1001~1003}；code>=900 時 @/api 攔截器自動彈出 subject/text，
 *     本頁僅負責清空重填。
 */

import IconSax from "@/component/IconSax.vue"
import Error404 from "@/component/Error.vue"
import CountryFlag from "@/component/CountryFlag.vue"

import { computed, nextTick, onMounted, reactive, ref } from "vue"
import { useRoute, useRouter } from "vue-router"
import { useI18n } from "vue-i18n"
import { sprintf } from "sprintf-js"
import { createLogin } from "@/api/module/session"
import http from "@/api"
import { useSetting } from "@/hook/useSetting"
import { usePermission } from "@/hook/usePermission"
import { useRegion } from "@/hook/useRegion"
import { useSwalAlert } from "@/hook/useSwalAlert"
import { EnumTheme } from "@/enum/theme"
import { RouteNameEnum } from "@/v2/enumerate/route"

// API（僅本頁使用 → 內聯實作，不另立 api/module）：驗證碼 /api/forgot/verification/edit/{uuid}
// 讀取與作廢皆 builtIn{loadingMask:false, failure:false}（頁面自行處理），送出用預設
const verifyUri = (uuid) => `/api/forgot/verification/edit/${uuid}`
const silent = { opts: { builtIn: { loadingMask: false, failure: false } } }
const apiVerifyFetch = (uuid) => http.get(verifyUri(uuid), {}, silent)
const apiVerifyStore = (uuid, params) => http.put(verifyUri(uuid), params, {})
const apiVerifyDestroy = (uuid) => http.delete(verifyUri(uuid), {}, silent)

const route = useRoute()
const router = useRouter()

// 六碼輸入框（v-for 收集為陣列）
const code = ref([])

const { t } = useI18n()
const { showWarning, showHandler } = useSwalAlert()
const { isLogin } = usePermission()
const { setTheme, isDarkTheme } = useSetting()
const { setRegion, getAllRegion, getCurrentRegion } = useRegion()

/**
 * 參數 - 本地
 */
const active = reactive({
  loaded: false,
  error: 0,
  success: 0,
  xhring: false,
  // 登入頁面資訊
  data: {
    organization: { seq: null, uuid: null, name: null },
    available: true,
    name: null,
    uuid: null,
    avatarUrl: null,
  },
  // 權杖過期倒數
  expire: {
    timer: null,
    value: 0,
  },
})

/**
 * 欄位
 */
const field = reactive({
  code: "",
})

/**
 * 銷毀（放棄重設 → 返回登入）
 */
const onDestroy = () => showHandler({
  subject: t("是否確定放棄重設密碼"),
  text: t("確認後將返回登入頁面"),
  completion: {
    success: async () => {
      await apiVerifyDestroy(route.params.uuid)
      await router.push({ name: "Login" })
    },
  },
})

/**
 * 提交
 */
const onSubmit = async () => {
  // 標示 - 進行中
  active.xhring = true

  const { data } = await apiVerifyStore(route.params.uuid, field)

  // 無法連線 / 伺服器錯誤（攔截器回 false）
  if (data === undefined) {
    return showWarning({
      subject: t("暫時無法使用"),
      text: t("請稍後再試或請聯絡管理人員"),
      completion: { done: () => (active.xhring = false) },
    })
  }

  // 正常（前往重設密碼頁）
  if (data.code === 200) {
    return router.push({
      name: "ForgotVerificationReset",
      params: { id: route.params.id, uuid: route.params.uuid, uid: data.data.uuid },
    })
  }

  // 驗證失敗（攔截器已彈出 subject/text）：清空並重新聚焦
  field.code = ""
  code.value.forEach((j) => (j.value = ""))
  active.xhring = false
  await nextTick()
  setTimeout(() => code.value[0].focus(), 150)
}

/**
 * 輸入（逐格填入 / 退格 / 自動跳格）
 */
const onKeyUp = async (e) => {
  const position = field.code.length - 1

  // 退格
  if (e.keyCode === 8 && field.code.length > 0) {
    field.code = field.code.substring(0, position)
    code.value[position].value = ""
  }
  // 其他
  else {
    // 最後輸入的不是數字 → 清掉
    if (!/^[0-9]$/.test(code.value[position + 1].value)) {
      code.value[position + 1].value = ""
    } else {
      field.code = ""
      code.value.forEach((j) => (field.code += !j.value ? "" : j.value))
    }
  }

  // 已滿六碼不再往下
  if (field.code.length >= 6) return

  await nextTick()
  code.value[field.code.length].focus()
}

/**
 * 輸入框停用判斷（僅開放「下一格」）
 */
const getDisabled = (e) => {
  if (e === 6 && field.code.length === 6) return false
  return e - 1 !== field.code.length
}

/**
 * 獲取 Logo
 */
const getLogo = computed(() => {
  if (active.data.organization.seq) return `/img/organization/${ active.data.organization.seq }/logo.png`
  return "/img/organization/1/logo.png"
})

/**
 * 獲取背景圖
 */
const getBackground = computed(() => {
  if (active.data.avatarUrl) return active.data.avatarUrl
  return "/img/avatar/holder/my.jpg"
})

/**
 * 存取 API - 教會 + 驗證單資訊
 */
const fetch = async () => {
  const { data } = await createLogin(route.params.id)

  // 返回錯誤不往下進行
  if (data?.data === undefined) return

  active.data.organization = data.data.organization
  active.data.available = data.data.available
  active.data.name = data.data.name
  active.data.uuid = data.data.uuid
  active.data.avatarUrl = data.data.avatarUrl

  // 先存取教會資訊 - 再存取確認資訊
  const { data: e } = await apiVerifyFetch(route.params.uuid)

  // 返回錯誤不往下進行或者已超時
  if (e?.data === undefined || e.data.expire === 0) {
    active.data.organization.seq = null
    active.data.organization.uuid = null
    active.data.organization.name = null
    active.data.available = false
    active.loaded = true
    return
  }

  // 登記 - 超時
  active.expire.value = e.data.expire

  // 啟動 - 超時 - 倒數
  active.expire.timer = setInterval(() => {
    active.expire.value -= 1
    if (active.expire.value <= 0) {
      clearInterval(active.expire.timer)
      showWarning({
        subject: t("重設密碼令牌已失效"),
        text: t("請重新進行忘記密碼程序"),
        completion: { done: () => router.replace({ name: "Login" }) },
      })
    }
  }, 1000)

  // 可以打開頁面了
  active.loaded = true

  // 等待畫面渲染後聚焦第一格
  await nextTick()
  setTimeout(() => code.value[0].focus(), 150)
}

/**
 * 生命週期 - 掛載完成
 */
onMounted(() => {
  // 如果已經登入, 前往控制面板
  if (isLogin.value) router.replace({ name: RouteNameEnum.ADMIN_DASHBOARD })
  // 未登入
  else fetch()
})

</script>

<style scoped lang="scss">
// 六碼輸入框
.code-input {
  width: 3.25rem;
  height: 4.5rem;
  border-radius: 0.4rem;
  border: 1px solid #efefef;
  background: #fff;
  text-align: center;
  font-size: 2rem;
  font-weight: 550;
  color: #5e5873;
  outline: none;
  user-select: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus {
    border-color: #dda026;
    box-shadow: 0 0 0 3px rgba(221, 160, 38, 0.18);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
}

// 送出按鈕（品牌金漸層）
.f-btn {
  width: 100%;
  border: 0;
  border-radius: 0.5rem;
  padding: 0.7rem 1rem;
  font-size: 0.95rem;
  font-weight: 600;
  color: #fff;
  cursor: pointer;
  background: linear-gradient(118deg, #dda026, rgba(221, 160, 38, 0.7));
  box-shadow: 0 8px 16px -6px rgba(221, 160, 38, 0.5);
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
}

// 深色模式
:where(.tw-dark-layout) {
  .code-input {
    border-color: #3b4253;
    background: #283046;
    color: #d0d2d6;
  }
}
</style>
